import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { studioAuth, fetchMe, fetchMyUnreadCount, pingUserHeartbeat, fetchPerksUnreadCount } from "../lib/studioApi";
import { Home, MessageCircle, Sparkles } from "lucide-react";
import NotificationBell from "../components/studio/NotificationBell";
import AccountMenu from "../components/studio/AccountMenu";
import BrandLogo from "../components/BrandLogo";
import useHeartbeat from "../hooks/useHeartbeat";

function fmtBadge(n) {
  if (!n || n <= 0) return null;
  return n > 9 ? "9+" : String(n);
}

export default function StudioLayout() {
  const navigate = useNavigate();
  const [me, setMe] = useState(studioAuth.getUser());
  const [unread, setUnread] = useState(0);
  const [perksUnread, setPerksUnread] = useState(0);

  // Heartbeat for "online" presence (every 60s)
  useHeartbeat(() => (studioAuth.getToken() ? pingUserHeartbeat() : null), 60000);

  useEffect(() => {
    if (!studioAuth.getToken()) {
      navigate("/studio/login", { replace: true });
      return undefined;
    }
    fetchMe()
      .then((u) => {
        setMe(u);
        studioAuth.setUser(u);
      })
      .catch(() => {
        studioAuth.clear();
        navigate("/studio/login", { replace: true });
      });

    const tick = () => {
      fetchMyUnreadCount().then(setUnread).catch(() => {});
      fetchPerksUnreadCount().then(setPerksUnread).catch(() => {});
    };
    tick();
    const id = setInterval(tick, 8000);
    return () => clearInterval(id);
  }, [navigate]);

  const logout = () => {
    studioAuth.clear();
    navigate("/studio/login", { replace: true });
  };

  const refreshMe = (updated) => setMe(updated);

  const navItem = ({ isActive }) =>
    `relative flex items-center gap-2 px-3 md:px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
      isActive ? "bg-white/10 text-paul-yellow" : "text-white/70 hover:bg-white/5 hover:text-white"
    }`;

  const unreadBadge = fmtBadge(unread);
  const perksBadge = fmtBadge(perksUnread);

  return (
    <div className="min-h-screen bg-[#0b0f1e] text-white flex flex-col">
      <header className="bg-[#10162a]/95 backdrop-blur border-b border-white/5 sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 md:gap-4">
            <BrandLogo variant="yellow" className="h-7 md:h-8" />
            <span className="hidden md:inline text-white/30 text-sm">/ Studio</span>
          </div>
          <nav className="flex items-center gap-1">
            <NavLink to="/studio" end className={navItem}>
              <Home className="w-4 h-4" />
              <span className="hidden sm:inline">Home</span>
            </NavLink>
            <NavLink to="/studio/messages" className={navItem}>
              <MessageCircle className="w-4 h-4" />
              <span className="hidden sm:inline">Messages</span>
              {unreadBadge && (
                <span
                  data-testid="studio-nav-unread-badge"
                  className="bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center"
                >
                  {unreadBadge}
                </span>
              )}
            </NavLink>
            <NavLink to="/studio/perks" className={navItem} data-testid="studio-nav-perks">
              <Sparkles className="w-4 h-4" />
              <span className="hidden sm:inline">Perks</span>
              {perksBadge && (
                <span
                  data-testid="studio-nav-perks-badge"
                  className="bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center"
                >
                  {perksBadge}
                </span>
              )}
            </NavLink>
            <NotificationBell />
          </nav>
          <div className="flex items-center gap-3">
            <div className="hidden md:block text-right">
              <div className="text-xs text-white/40">Signed in as</div>
              <div className="text-sm text-white/90 font-medium">{me?.artistName || me?.name}</div>
            </div>
            <AccountMenu
              user={me}
              onLogout={logout}
              profilePath="/studio/profile"
              settingsPath="/studio/settings"
              theme="dark"
              testIdPrefix="studio-account-menu"
            />
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-5xl mx-auto w-full px-4 md:px-6 py-6 md:py-10">
        <Outlet context={{ me, refreshMe }} />
      </main>
    </div>
  );
}
